import {
  ReactElement,
  memo, useCallback, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import { DateTime } from 'luxon';

import copy from 'clipboard-copy';

import { useDispatcher } from 'store/utils/redux/hooks';
import * as invitationStore from 'store/nodes/invitation';

import Button from 'components/Button';

import { ReactComponent as PointIcon } from '../assets/point.svg';

const Respond = (): ReactElement => {
  const classes = useStyles();
  const dispatcher = useDispatcher();

  const eventData = useSelector(invitationStore.selectors.getEvent);
  const isLoadingAttending = useSelector(invitationStore.selectors.isLoading('attending'));
  const isLoadingNotAttending = useSelector(invitationStore.selectors.isLoading('notAttending'));

  const handleAttending = useCallback(() => {
    dispatcher.invitation.sendAnswerAttending();
  }, []);

  const handleNotAttending = useCallback(() => {
    dispatcher.invitation.sendAnswerNotAttending();
  }, []);

  const handleCopy = useCallback((event: any) => {
    copy(event.target.textContent).then(() => {
      dispatcher.toast.showToast('copied', 1000);
    });
  }, []);

  const renderAddress = useMemo(() => {
    if (!eventData?.locationName && !eventData?.locationAddress) {
      return null;
    }
    if (!eventData?.locationName && eventData?.locationAddress) {
      return (
        <div className={classes.blockLocation}>
          <div className={classes.blockLocationPoint}><PointIcon fill="#20B2A4" /></div>
          <div className={classes.blockLocationTextAddress} onClick={handleCopy}>{eventData?.locationAddress}</div>
        </div>
      );
    }
    if (eventData?.locationName && !eventData?.locationAddress) {
      return (
        <div className={classes.blockLocation}>
          <div className={classes.blockLocationPoint}><PointIcon fill="#20B2A4" /></div>
          <div>{eventData?.locationName}</div>
        </div>
      );
    }
    return (
      <div className={classes.blockLocation}>
        <div className={classes.blockLocationPoint}><PointIcon fill="#20B2A4" /></div>
        <div>
          <div>{eventData?.locationName}</div>
          <div className={classes.blockLocationTextAddress} onClick={handleCopy}>{eventData?.locationAddress}</div>
        </div>
      </div>
    );
  }, []);

  const time = useMemo(() => {
    if (!eventData?.startTime && !eventData?.endTime) {
      return '';
    }
    const startTime = DateTime.fromISO(eventData.startTime, { locale: 'en' });
    if (!eventData?.endTime) {
      return startTime.toLocaleString(DateTime.DATETIME_MED);
    }
    const endTime = DateTime.fromISO(eventData.endTime, { locale: 'en' });
    return `${startTime.toFormat('MMM d, yyyy, h:mm a')} - ${endTime.toFormat('h:mm a')}`;
  }, [eventData?.startTime, eventData?.endTime]);

  return (
    <>
      <div className={classes.title}>
        Respond to invitation
      </div>
      {eventData?.title && (
        <div className={classes.block}>
          <div className={classes.blockTitle}>Invitation:</div>
          <div className={classes.blockText}>{eventData.title}</div>
        </div>
      )}
      {eventData?.startTime && (
        <div className={classes.block}>
          <div className={classes.blockTitle}>Data and Time:</div>
          <div className={classes.blockText}>{time}</div>
        </div>
      )}
      {renderAddress}
      <div className={classes.block}>
        <div className={classes.blockTitle}>Will you be able to attend this event?</div>
      </div>
      <div className={classes.buttons}>
        <Button
          width="full"
          height={44}
          noPadding
          htmlColor="#868686"
          isDisabled={isLoadingAttending}
          isLoading={isLoadingNotAttending}
          onPress={handleNotAttending}
        >
          No
        </Button>
        <Button
          width="full"
          height={44}
          noPadding
          htmlColor="#20B2A4"
          isDisabled={isLoadingNotAttending}
          isLoading={isLoadingAttending}
          onPress={handleAttending}
        >
          Yes - Attending
        </Button>
      </div>
    </>
  );
};

const useStyles = createUseStyles({
  title: {
    fontWeight: '700',
    fontSize: '2.2rem',
    lineHeight: 1.3,
    textAlign: 'left',
    color: '#454545',
  },
  block: {
    marginTop: '2.4rem',
    color: '#454545',
  },
  blockTitle: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  blockText: {
    marginTop: '0.2rem',
    fontSize: '1.5rem',
  },
  blockLocation: {
    display: 'flex',
    marginTop: '2.4rem',
    fontWeight: 400,
    color: '#454545',
    fontSize: '1.4rem',
    justifyContent: 'stretch',
    alignItems: 'center',
  },
  blockLocationPoint: {
    width: '1.5rem',
    height: '2.2rem',
    marginRight: '1rem',
  },
  blockLocationTextAddress: {
    cursor: 'pointer',
    color: '#20B2A4',
    fontWeight: 600,
    textDecoration: 'underline',
  },
  buttons: {
    '&': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: '1.6rem',
    },
    '& > button': {
      marginRight: '1.6rem',
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
});

export default memo(Respond);
